import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, RotateCcw, ChevronRight, Eye, Check, Code2, Plus, Trash2, Pencil } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Phase 61 — Company-scoped email templates editor.
 *
 * Lists every template in the catalog grouped by category. Each row shows
 * an enable toggle (employer-only), an Edit button, and a "Customized"
 * badge when the company has overrides on top of the default. The Edit
 * drawer renders a live preview by hydrating placeholders with sample data.
 */

const SAMPLE_VARS = {
    candidate_name: "Asha Iyer",
    job_title: "Senior Backend Engineer",
    company_name: "Acme Talent",
    portal_link: "https://talent.portalwiz.com/seeker/applications",
    round_name: "Tech Round 1",
    scheduled_at_human: "Wed, 11 Mar 2026 · 14:30 UTC",
    duration_minutes: "45",
    mode: "Video",
    meeting_block: "<p><strong>Join link:</strong> <a href='#'>https://meet.google.com/abc-defg-hij</a></p>",
    interviewers: "Rohan Mehta, Priya Sharma",
    offer_link: "https://talent.portalwiz.com/offers/sample",
    recruiter_name: "Rohan Mehta",
    message_preview: "Hey — loved your background, would you be open to a 20-min intro this week?",
    signature_html: (
        "<div style='margin-top:28px;padding-top:18px;border-top:1px solid #e5e7eb;" +
        "font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#334155;'>" +
        "<div style='font-weight:700;color:#0f172a;'>You (logged-in user) " +
        "<span style='font-weight:400;color:#64748b;'>· Recruiter</span></div>" +
        "<div style='margin-top:4px;color:#475569;'>Senior Talent Partner — Acme Talent</div>" +
        "</div>"
    ),
};

function applyPlaceholders(template, vars = SAMPLE_VARS) {
    if (!template) return "";
    return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ""));
}

export default function CompanyEmailTemplates() {
    const { user } = useAuth();
    const [byCategory, setByCategory] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(null); // {name, subject, body_html, category}

    const canEdit = user?.role === "employer";

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/company/email-templates");
            setByCategory(data?.by_category || {});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const toggleEnabled = async (tpl, next) => {
        if (!canEdit) return toast.error("Only the employer admin can edit templates");
        try {
            await api.patch(`/company/email-templates/${encodeURIComponent(tpl.key)}`, { enabled: next });
            toast.success(next ? "Template enabled" : "Template disabled");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await api.patch(`/company/email-templates/${encodeURIComponent(editing.key)}`, {
                subject: editing.subject,
                body_html: editing.body_html,
                enabled: editing.enabled,
            });
            toast.success("Saved");
            setEditing(null);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const resetTemplate = async () => {
        if (!editing) return;
        if (!window.confirm("Reset to default content? Your customizations will be lost.")) return;
        setBusy(true);
        try {
            await api.post(`/company/email-templates/${encodeURIComponent(editing.key)}/reset`);
            toast.success("Reset to default");
            setEditing(null);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const deleteCustom = async () => {
        if (!editing || !editing.is_custom) return;
        if (!window.confirm(`Permanently delete "${editing.name}"? This cannot be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/company/email-templates/custom/${encodeURIComponent(editing.key)}`);
            toast.success("Deleted");
            setEditing(null);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const createNow = async () => {
        if (!creating) return;
        const name = (creating.name || "").trim();
        const subject = (creating.subject || "").trim();
        const body_html = (creating.body_html || "").trim();
        if (!name || !subject || !body_html) {
            toast.error("Name, subject and body are all required");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/company/email-templates/custom", {
                name, subject, body_html, category: (creating.category || "Custom").trim() || "Custom",
            });
            toast.success("Template created");
            setCreating(null);
            await load();
            if (data?.template) setEditing({ ...data.template, is_custom: true, placeholders: ["candidate_name", "job_title", "company_name", "portal_link", "signature_html"] });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const categories = useMemo(() => Object.keys(byCategory), [byCategory]);

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8 space-y-8" data-testid="company-email-templates-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Workspace · Email Templates</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Email templates</h1>
                <p className="text-muted-foreground mt-2 max-w-3xl">
                    Customize the subject and HTML body of every transactional email Portalwiz sends on your behalf —
                    stage changes, interview invites, offers, and recruiter messages. Disable any template to silence
                    that event for your company. Placeholders like <span className="font-mono text-foreground">{"{candidate_name}"}</span> are auto-filled at send time.
                </p>
                {!canEdit && (
                    <div className="mt-3 inline-block text-[11px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-2.5 py-1">
                        Read-only · only your employer admin can edit
                    </div>
                )}
                {canEdit && (
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <Button
                            className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9"
                            onClick={() => setCreating({ name: "", subject: "", body_html: "<p>Hi {candidate_name},</p><p>Write your message here…</p><p>{signature_html}</p>", category: "Custom" })}
                            data-testid="tpl-new-btn"
                        >
                            <Plus className="w-4 h-4 mr-1.5" /> New template
                        </Button>
                        <a
                            href="/company/email-signature"
                            className="h-9 px-3 inline-flex items-center text-xs font-mono uppercase tracking-widest border border-border bg-white hover:bg-secondary"
                            data-testid="tpl-signature-link"
                        >
                            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit signature
                        </a>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground font-mono">Loading...</div>
            ) : categories.length === 0 ? (
                <div className="text-sm text-muted-foreground font-mono">No templates configured.</div>
            ) : (
                <div className="space-y-10">
                    {categories.map((cat) => (
                        <section key={cat} data-testid={`tpl-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                            <h2 className="font-display font-bold text-xl mb-3">{cat}</h2>
                            <div className="border border-border bg-white divide-y divide-border">
                                {(byCategory[cat] || []).map((t) => (
                                    <div key={t.key} className="p-4 grid grid-cols-12 gap-3 items-center" data-testid={`tpl-row-${t.key}`}>
                                        <div className="col-span-12 md:col-span-7 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Mail className="w-4 h-4 text-ikb" />
                                                <div className="font-medium text-sm">{t.name}</div>
                                                {t.is_customized && (
                                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5" data-testid={`tpl-customized-${t.key}`}>
                                                        <Check className="w-3 h-3 inline-block mr-1 -mt-0.5" />Customized
                                                    </span>
                                                )}
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border px-2 py-0.5">
                                                    → {t.audience}
                                                </span>
                                            </div>
                                            <div className="text-xs font-mono text-muted-foreground mt-1 truncate">{t.subject}</div>
                                        </div>
                                        <div className="col-span-6 md:col-span-2 flex items-center gap-2">
                                            <Switch
                                                checked={t.enabled !== false}
                                                onCheckedChange={(v) => toggleEnabled(t, v)}
                                                disabled={!canEdit}
                                                data-testid={`tpl-enabled-${t.key}`}
                                            />
                                            <span className="text-xs font-mono text-muted-foreground">
                                                {t.enabled === false ? "Off" : "On"}
                                            </span>
                                        </div>
                                        <div className="col-span-6 md:col-span-3 flex justify-end">
                                            <Button
                                                variant="outline"
                                                className="rounded-none h-9"
                                                onClick={() => setEditing({ ...t })}
                                                disabled={!canEdit}
                                                data-testid={`tpl-edit-${t.key}`}
                                            >
                                                Edit <ChevronRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {editing && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-center p-2 sm:p-6 overflow-y-auto"
                    onClick={() => setEditing(null)}
                    data-testid="tpl-editor-overlay"
                >
                    <div
                        className="bg-white border border-border w-full max-w-5xl my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-4 border-b border-border bg-secondary/40 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest font-mono text-ikb">{editing.category}</div>
                                <h2 className="font-display font-bold text-lg truncate">{editing.name}</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                {editing.is_custom && (
                                    <Button variant="outline" className="rounded-none h-9 text-rose-700 border-rose-300 hover:bg-rose-50" onClick={deleteCustom} disabled={busy} data-testid="tpl-delete">
                                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                                    </Button>
                                )}
                                {editing.is_customized && !editing.is_custom && (
                                    <Button variant="outline" className="rounded-none h-9" onClick={resetTemplate} disabled={busy} data-testid="tpl-reset">
                                        <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
                                    </Button>
                                )}
                                <Button variant="outline" className="rounded-none h-9" onClick={() => setEditing(null)}>Cancel</Button>
                                <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" onClick={save} disabled={busy} data-testid="tpl-save">
                                    {busy ? "Saving…" : "Save changes"}
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">
                            <div className="p-5 space-y-4">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={editing.enabled !== false}
                                        onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                                        data-testid="tpl-editor-enabled"
                                    />
                                    <Label className="text-xs uppercase tracking-widest font-bold">
                                        {editing.enabled === false ? "Disabled" : "Enabled"}
                                    </Label>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest font-bold">Subject</Label>
                                    <Input
                                        value={editing.subject}
                                        onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                                        className="rounded-none h-10 mt-2 font-mono text-sm"
                                        data-testid="tpl-editor-subject"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs uppercase tracking-widest font-bold">Body</Label>
                                        <button
                                            type="button"
                                            onClick={() => setEditing((e) => ({ ...e, _htmlMode: !e._htmlMode }))}
                                            className="text-[11px] font-mono text-muted-foreground hover:text-ikb inline-flex items-center gap-1"
                                            data-testid="tpl-editor-html-toggle"
                                        >
                                            <Code2 className="w-3 h-3" /> {editing._htmlMode ? "Visual editor" : "Edit raw HTML"}
                                        </button>
                                    </div>
                                    {editing._htmlMode ? (
                                        <Textarea
                                            value={editing.body_html}
                                            onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
                                            className="rounded-none mt-2 min-h-[320px] font-mono text-xs"
                                            data-testid="tpl-editor-body"
                                        />
                                    ) : (
                                        <div className="mt-2" data-testid="tpl-editor-body-rich">
                                            <RichTextEditor
                                                value={editing.body_html}
                                                onChange={(v) => setEditing({ ...editing, body_html: v })}
                                                placeholder="Compose the email body…"
                                                minHeight={300}
                                                testId="tpl-editor-body"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest font-bold">Available placeholders</Label>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {(editing.placeholders || []).map((p) => (
                                            <code
                                                key={p}
                                                className="text-[11px] font-mono px-2 py-1 border border-border bg-secondary cursor-pointer hover:bg-ikb hover:text-white"
                                                title="Click to copy"
                                                onClick={() => {
                                                    navigator.clipboard?.writeText(`{${p}}`);
                                                    toast.success(`Copied {${p}}`);
                                                }}
                                            >
                                                {`{${p}}`}
                                            </code>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 space-y-3 bg-secondary/30">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-muted-foreground">
                                    <Eye className="w-3.5 h-3.5" /> Live preview · sample data
                                </div>
                                <div className="border border-border bg-white p-4">
                                    <div className="text-xs font-mono text-muted-foreground border-b border-border pb-2 mb-3">
                                        <strong>Subject:</strong> {applyPlaceholders(editing.subject)}
                                    </div>
                                    <div
                                        className="prose prose-sm max-w-none text-sm leading-relaxed"
                                        data-testid="tpl-editor-preview"
                                        dangerouslySetInnerHTML={{ __html: applyPlaceholders(editing.body_html) }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {creating && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3"
                     onClick={() => !busy && setCreating(null)} data-testid="tpl-new-overlay">
                    <div className="bg-white border border-border w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-border bg-secondary/40">
                            <div className="text-xs uppercase tracking-widest font-mono text-ikb">New custom template</div>
                            <h2 className="font-display font-bold text-lg">Add your own template</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Use placeholders like <code>{"{candidate_name}"}</code>, <code>{"{job_title}"}</code>, <code>{"{company_name}"}</code> and <code>{"{signature_html}"}</code> — they fill in automatically at send time.
                            </p>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Name</Label>
                                <Input
                                    value={creating.name}
                                    onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                                    placeholder="e.g. Post-interview thank-you"
                                    className="rounded-none h-10 mt-2"
                                    data-testid="tpl-new-name"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Subject</Label>
                                <Input
                                    value={creating.subject}
                                    onChange={(e) => setCreating({ ...creating, subject: e.target.value })}
                                    placeholder="e.g. Loved chatting about {job_title}, {candidate_name}"
                                    className="rounded-none h-10 mt-2 font-mono text-sm"
                                    data-testid="tpl-new-subject"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Body (HTML)</Label>
                                <Textarea
                                    value={creating.body_html}
                                    onChange={(e) => setCreating({ ...creating, body_html: e.target.value })}
                                    className="rounded-none mt-2 min-h-[200px] font-mono text-xs"
                                    data-testid="tpl-new-body"
                                />
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
                            <Button variant="outline" className="rounded-none h-9" disabled={busy} onClick={() => setCreating(null)}>
                                Cancel
                            </Button>
                            <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" disabled={busy} onClick={createNow} data-testid="tpl-new-create">
                                {busy ? "Creating…" : "Create template"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
